/**
 * Resumable upload via tus protocol.
 *
 * Why tus:
 *   - Files >100MB pass through Cloudflare (chunks are 8MB each).
 *   - Network blips auto-retry per chunk; no full-file restart.
 *   - Server processes chunks streaming, no Buffer materialization
 *     of the assembled file.
 *
 * Two entry points:
 *   - `buildTusUpload`  — returns a configured tus.Upload instance.
 *     Use this when you need pause/resume/abort control (e.g. the
 *     UploadManager provider that owns long-lived sessions).
 *   - `uploadFileWithTus` — Promise wrapper around buildTusUpload
 *     for fire-and-forget callers.
 */
import * as tus from 'tus-js-client'
import { getApiBase, getDefaultHeaders, identityHeaders, type RequestIdentity } from '@papan/api-client'
import { MalwareDetectedError, type ConflictStrategy } from './index.js'
import { toWireConflictStrategy } from '@papan/types'

export interface TusUploadOptions {
  description?: string
  conflictStrategy?: ConflictStrategy | 'fail'
  /** When set with `replace`, finalize verifies this file is still
   *  the one with the same name in the folder. Race guard for explicit overwrites. */
  targetFileId?: string
  /** Subpath under parentId — folders auto-created server-side */
  path?: string
  onProgress?: (loaded: number, total: number) => void
  signal?: AbortSignal
  uploadId?: string
  /** #1316 review round 2 (owner, 2026-10-28): the identity the person STARTED the upload as (`batch.opts().identity`
   *  of the upload's `pinnedBatch`, read when the file is sent) — a file still queued or sending at a tenant / identity
   *  switch is sent as it, never as the next session (the old folder ids under the new tenant, a root upload into its
   *  drive). */
  identity?: RequestIdentity
}

export interface TusUploadResult {
  fileId: string
  name: string
  path?: string
  versionId: string
  skipped?: boolean
  /** #1090 — a large Replace of an app's live database went to a background job (202): the upload is done, the
   *  Replace's outcome arrives later (the db-operation notice and the bell). `versionId` is empty. */
  queued?: { recordId: string }
}

export interface TusUploadCallbacks {
  onProgress?: (loaded: number, total: number) => void
  onSuccess?: (result: TusUploadResult) => void
  onError?: (err: Error) => void
}

const CHUNK_SIZE = 8 * 1024 * 1024
/** The first five retries are for any retryable failure; the long tail only for a database still being checked
 *  (#1074 — a large app db's check can outlast a request: 503 `ENGINE_DB_VERIFYING`, the check goes on server-side and
 *  a retry a minute later reuses its result). */
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 30000, 60000, 60000, 60000, 60000]
const GENERAL_RETRIES = 5

/** tus-js-client's own retry rule (not 4xx except 409/423, and online), with #1074's long tail for "still verifying". */
export function shouldRetryTusUpload(status: number, body: string | null | undefined, retryAttempt: number): boolean {
  if (status === 503 && isVerifyingAnswer(body)) return true
  // #1090 — the database is being replaced by a background job (minutes): a retry would re-send the whole large file
  // into the same refusal. The person is told; they upload again once that Replace has ended. #1134: a save still
  // LANDING on disk (`landing`, seconds) is retried like any busy answer.
  if (answerCode(body) === 'ENGINE_DB_REPLACING' && !isLandingAnswer(body)) return false
  if (retryAttempt >= GENERAL_RETRIES) return false
  const online = typeof navigator === 'undefined' || navigator.onLine !== false
  return (!(status >= 400 && status < 500) || status === 409 || status === 423) && online
}

function isLandingAnswer(body: string | null | undefined): boolean {
  if (!body) return false
  try { return (JSON.parse(body) as { landing?: unknown }).landing === true } catch { return false }
}

function isVerifyingAnswer(body: string | null | undefined): boolean {
  return answerCode(body) === 'ENGINE_DB_VERIFYING'
}

function answerCode(body: string | null | undefined): unknown {
  if (!body) return undefined
  try { return (JSON.parse(body) as { code?: unknown }).code } catch { return undefined }
}

/**
 * Build a configured tus.Upload instance. The caller controls start/pause/resume.
 * Useful for an upload manager that needs long-lived sessions.
 */
export function buildTusUpload(
  parentId: string | null,
  fileName: string,
  content: Blob | File,
  options: TusUploadOptions & TusUploadCallbacks = {},
): tus.Upload {
  const endpoint = `${getApiBase()}/api/upload/tus/`
  let lastResponseBody: string | null = null

  const metadata: Record<string, string> = { filename: fileName }
  if (parentId) metadata.parentId = parentId
  // Replace goes out under its wire name (`replaceVersion`): an old server rejects it (rolling deploy).
  if (options.conflictStrategy) metadata.conflictStrategy = toWireConflictStrategy(options.conflictStrategy)
  if (options.targetFileId) metadata.targetFileId = options.targetFileId
  if (options.description) metadata.description = options.description
  if (options.path) metadata.path = options.path
  if (options.uploadId) metadata.clientUploadId = options.uploadId
  // The browser's own type for the file (File.type / Blob.type, from the OS
  // mime registry). Historically never sent, so the server fell back to a
  // 7-entry extension map and stored `application/octet-stream` for
  // everything else — every uploaded .mp4/.pdf/.png was typeless in the DB.
  // The server validates before trusting (pickUploadMime in tus.ts).
  if (content.type) metadata.filetype = content.type

  return new tus.Upload(content, {
    endpoint,
    chunkSize: CHUNK_SIZE,
    retryDelays: RETRY_DELAYS,
    onShouldRetry: (err, retryAttempt) => {
      const res = (err as tus.DetailedError).originalResponse
      let body: string | null = null
      try { body = res?.getBody?.() ?? null } catch { /* no body */ }
      return shouldRetryTusUpload(res?.getStatus?.() ?? 0, body, retryAttempt)
    },
    removeFingerprintOnSuccess: true,
    headers: options.identity ? identityHeaders(options.identity) : getDefaultHeaders(),
    metadata,
    // Auth is cookie-based. In dev the web (:7474) and API (:3001) are
    // cross-origin so cookies only flow when withCredentials is set on
    // the XHR. tus-js-client v4 doesn't expose this as an option, but
    // we can reach the underlying XHR via getUnderlyingObject().
    onBeforeRequest: (req) => {
      const xhr = req.getUnderlyingObject() as XMLHttpRequest | undefined
      if (xhr && 'withCredentials' in xhr) xhr.withCredentials = true
    },
    onAfterResponse: (_req, res) => {
      try { lastResponseBody = res.getBody() } catch { /* ignore */ }
    },
    onError: (err) => {
      let body: { error?: string; code?: string; threat?: string; scanner?: string } | null = null
      try {
        const original = (err as tus.DetailedError).originalResponse
        const raw = original?.getBody?.() ?? lastResponseBody
        if (raw) body = JSON.parse(raw)
      } catch { /* not JSON */ }
      const mapped = body?.threat
        ? new MalwareDetectedError(body.threat, body.scanner || 'unknown')
        : new Error(body?.error || err.message || 'Upload failed')
      // Keep the server's refusal code (FOLDER_BEING_DELETED, …), the same
      // field `request()` surfaces, so callers can branch on it rather than
      // on the message.
      if (typeof body?.code === 'string') (mapped as Error & { code?: string }).code = body.code
      options.onError?.(mapped)
    },
    onProgress: (bytesUploaded, bytesTotal) => {
      options.onProgress?.(bytesUploaded, bytesTotal)
    },
    onSuccess: () => {
      try {
        const data = lastResponseBody ? JSON.parse(lastResponseBody) : null
        if (data && data.success !== false) {
          options.onSuccess?.({
            fileId: data.fileId,
            name: data.name,
            path: data.path ?? undefined,
            versionId: data.versionId ?? '',
            skipped: data.skipped,
            ...(data.queued && typeof data.recordId === 'string' ? { queued: { recordId: data.recordId } } : {}),
          })
        } else {
          options.onError?.(new Error(data?.error || 'Upload failed'))
        }
      } catch {
        options.onError?.(new Error('Invalid response from server'))
      }
    },
  })
}

/**
 * Promise wrapper for fire-and-forget callers (filesApi.uploadFileWithProgress).
 * For long-lived sessions with pause/resume, use buildTusUpload directly.
 */
export function uploadFileWithTus(
  parentId: string | null,
  fileName: string,
  content: Blob | File,
  options?: TusUploadOptions,
): Promise<TusUploadResult> {
  return new Promise((resolve, reject) => {
    const upload = buildTusUpload(parentId, fileName, content, {
      ...options,
      onProgress: options?.onProgress,
      onSuccess: resolve,
      onError: reject,
    })

    if (options?.signal) {
      const onAbort = () => {
        upload.abort()
        reject(new Error('Upload cancelled'))
      }
      if (options.signal.aborted) {
        onAbort()
        return
      }
      options.signal.addEventListener('abort', onAbort, { once: true })
    }

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0])
        }
        upload.start()
      })
      .catch(() => upload.start())
  })
}

/**
 * Drop any tus session URLs in localStorage that are stale or unrecoverable.
 * Files can't be persisted across page reload, so previous uploads can't be
 * resumed even if their URL is still valid — clearing keeps localStorage tidy.
 *
 * Returns the count of cleared entries (for logging).
 */
export async function clearStaleTusSessions(): Promise<number> {
  try {
    const previous = await tus.defaultOptions.urlStorage?.findAllUploads?.()
    if (!previous || previous.length === 0) return 0
    let cleared = 0
    for (const entry of previous) {
      try {
        await tus.defaultOptions.urlStorage?.removeUpload?.(entry.urlStorageKey)
        cleared++
      } catch { /* skip */ }
    }
    return cleared
  } catch {
    return 0
  }
}
